import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	isReadOnly as computeReadOnly,
	canManageDestinations as computeCanManageDestinations,
	canManageLifecycleAndDelivery as computeCanManageLifecycle,
} from "../../utilities/permissions";
import Toast from "../../components/toast";
import { concurrentChangeToast } from "../../components/toast/concurrentChange";
import {
	getCohortbyCohortID,
	updateCohort,
	createCohort,
	getSignalCohortSizing,
	isConcurrentChange,
} from "../../services/cohort";
import { DeleteCohort } from "../../services/delete";
import { DestinationsModal } from "../../components/builder-v2/modals";
import BasicInput from "../../components/inputs/basic";
import { formatDv360SlotSuffix } from "../../utilities/dv360Slots";
import { isEquativMethodId } from "../../utilities/equativPrice";
import { getTag } from "../../services/tag";
import { getSession } from "../../services/auth";
import PropTypes from "prop-types";
import Button from "../../components/inputs/button";
import Slider from "../../components/inputs/slider";
// ASB-4018 page structures. Storybook -> Layout / Audience Page composes these exactly as
// this page does; the modeled page migrates to them next.
import AudienceHeader from "../../components/layout/audienceHeader";
import MetricsBar from "../../components/layout/metricsBar";
import BuilderCard from "../../components/layout/builderCard";

import "./index.scss";

// Like a contextual audience, a signal audience has NO TTL. Membership is transient -- a
// user matches while viewing a page whose URL clears the affinity threshold, and there is
// nothing to expire afterwards. Hence no Advanced Options accordion here and no `ttl` on
// save; the column keeps the publisher default it was created with, exactly as it does for
// transient composite audiences.

// abuilder's lift index, where 100 is the site baseline. Measured against silo38 on
// 2026-09-22 (331,803 rows): p50 144, p75 278, p90 472, p95 707, p99 2,095, max 65,625 —
// so these marks span roughly the 45th to 97th percentile.
const affinitySliderValues = [100, 125, 150, 200, 300, 500, 1000];
const AFFINITY_INFO =
	"How much more likely this audience is to visit a page than the site average. 100 is the site baseline, so 200 means twice as likely.";
const DEFAULT_AFFINITY = 150;
// The slider fires a live Snowflake query, unlike the modeled slider which indexes a
// pre-computed curve. Long enough to coalesce a drag across several marks.
const SIZING_DEBOUNCE_MS = 400;

function Signal({ editMode }) {
	const [cohort, setCohort] = useState(null);
	const params = useParams();
	const uniqueId = params.id ?? false ? params.id.split("-").join(" ") : null;
	const [toast, setToast] = useState(null);
	const [audienceName, setAudienceName] = useState("");
	const [audienceDescription, setAudienceDescription] = useState("");
	const [loading, setLoading] = useState(false);
	const [selectedAffinity, setSelectedAffinity] = useState(DEFAULT_AFFINITY);
	const navigate = useNavigate();
	const [readOnly, setReadOnly] = useState(false);
	// Destinations. A signal audience can already be pushed and pulled through the Manager's
	// bulk actions, the Excel import and the API -- #4555 accepts all three by design -- so the
	// page has to show and edit them too, or someone pushes from the Manager and finds nothing
	// here saying it went anywhere.
	const [canManageDestinations, setCanManageDestinations] = useState(false);
	const [canManageLifecycle, setCanManageLifecycle] = useState(false);
	const [activationMethods, setActivationMethods] = useState([]);
	// The version this audience was loaded at, sent back on save so a save someone else made
	// in the meantime is refused rather than overwritten (ASB-4801).
	const [loadedVersion, setLoadedVersion] = useState(null);
	const [gamNetworkCodes, setGamNetworkCodes] = useState([]);
	const [gamNetworkSelectionMode, setGamNetworkSelectionMode] = useState("all");
	const [gamNetworkSubset, setGamNetworkSubset] = useState([]);
	const [destinationsModalOpen, setDestinationsModalOpen] = useState(false);
	const [isActive, setIsActive] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [showCopyModal, setShowCopyModal] = useState(false);
	const [copyName, setCopyName] = useState("");
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showArchiveModal, setShowArchiveModal] = useState(false);
	// Live reach at the selected threshold. status distinguishes a real zero from a silo
	// that was never scored ("unavailable") — the two must not look alike.
	const [sizing, setSizing] = useState(null);
	const [sizingLoading, setSizingLoading] = useState(false);
	// Guards against a slow earlier request landing after a faster later one and
	// overwriting the number the slider is actually showing.
	const sizingSeq = useRef(0);

	useEffect(() => {
		uniqueId ? verifyAccess() : "";
	}, []);

	useEffect(() => {
		const pageTitle = editMode ? "Edit Audience" : "View Audience";
		document.title = cohort ? `${pageTitle} | ${cohort.name}` : `${pageTitle}`;
	}, [cohort]);

	const verifyAccess = async () => {
		const session = getSession();
		if (!session) return;

		const isReadOnlyUser = computeReadOnly(session.role_id);
		setReadOnly(isReadOnlyUser);
		setCanManageDestinations(computeCanManageDestinations(session.role_id));
		setCanManageLifecycle(computeCanManageLifecycle(session.role_id));
		// A read-only user reaching /audience-signal/edit/:id directly would otherwise get an
		// editable name, description, slider and Update button, and only find out the role
		// cannot save when the API refuses the PUT. The UI must not offer what the endpoint
		// rejects (REVIEW.md 2.2), so send them to the view page instead. The modeled edit
		// page has the same hole; it is not fixed here, but this page should not inherit it.
		if (editMode && isReadOnlyUser) {
			navigate(`/audience-signal/view/${uniqueId}`, { replace: true });
			return;
		}
		try {
			const _cohort = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));

			if (_cohort.data.cohort_type != "signal") {
				setCohort(false);
				setToast({
					message: `${uniqueId} is not a valid signal audience.`,
					status: "Invalid",
					title: "Error",
				});
				return;
			}

			const tagId = _cohort.data.tagId;
			const tag = await getTag(tagId);
			const settings = tag.data.publisherSettings ?? {};
			if (!settings.signalEnabled) {
				setCohort(false);
				setToast({
					message: "Signal audiences are not enabled for this publisher.",
					status: "Invalid",
					title: "Not available",
				});
				return;
			}
			if (tag.data.integrations && tag.data.integrations.gam) {
				setGamNetworkCodes(tag.data.integrations.gam.networkCodes ?? []);
			}
			setAudienceName(_cohort.data.name);
			setAudienceDescription(_cohort.data.description);
			setLoadedVersion(_cohort.data.version ?? null);
			setActivationMethods(_cohort.data.activation_methods ?? []);
			setGamNetworkSelectionMode(_cohort.data.gamNetworkSelectionMode ?? "all");
			setGamNetworkSubset(_cohort.data.gamNetworkSubset ?? []);
			setIsActive(_cohort.data.active !== false);
			const affinity = _cohort.data.signal_conditions?.affinity_score ?? DEFAULT_AFFINITY;
			setSelectedAffinity(affinity);
			setCohort(_cohort.data);
			fetchSizing(_cohort.data, affinity);
		} catch (error) {
			setCohort(false);
			setToast({
				message: `No Audience was found with the id: ${uniqueId}`,
				status: "Invalid",
				title: "Error",
			});
		}
	};

	const fetchSizing = async (_cohort, affinity) => {
		const baseCohortId = _cohort?.signal_conditions?.base_cohort_id;
		if (!baseCohortId) return;

		const seq = ++sizingSeq.current;
		setSizingLoading(true);
		try {
			const response = await getSignalCohortSizing({
				data: [
					{
						tagId: parseInt(_cohort.tagId),
						base_cohort_id: baseCohortId,
						affinity_score: affinity,
					},
				],
			});
			const result = await response.json();
			if (seq !== sizingSeq.current) return; // a newer request already answered

			setSizing(result);
			if (result.status === "timeout") {
				setToast({
					message: "Timeout exceeded when estimating reach. Please try again later.",
					status: "Invalid",
					title: "Timeout exceeded",
				});
			} else if (result.status === "failed") {
				setToast({
					message:
						"Error encountered when estimating reach. Please reach out to your ArcSpan representative for assistance.",
					status: "Invalid",
					title: "Error encountered",
				});
			}
		} catch (error) {
			if (seq !== sizingSeq.current) return;
			const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
			const message = errors
				? errors.map((e) => e[1]).join("\n")
				: error.data?.message || error.message || "Please try again later.";
			setToast({ message: message, status: "Invalid", title: errors ? error.data.message : "Error" });
		} finally {
			if (seq === sizingSeq.current) setSizingLoading(false);
		}
	};

	// Debounce the slider: each settle is one Snowflake query.
	useEffect(() => {
		if (!cohort) return;
		const handle = setTimeout(() => fetchSizing(cohort, selectedAffinity), SIZING_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [selectedAffinity]);

	const toastClose = () => {
		setToast(null);
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		setLoading(true);
		const payload = {
			data: [
				{
					name: audienceName,
					description: audienceDescription,
					signal_conditions: {
						affinity_score: selectedAffinity,
					},
					...(loadedVersion != null && { version: loadedVersion }),
				},
			],
		};
		updateCohort(payload, uniqueId, localStorage.getItem("activeSilo"))
			.then((r) => {
				r.json().then((r) => {
					// Until the view page re-reads, its actions must send the version this save left.
					if (r?.version != null) setLoadedVersion(r.version);
					setLoading(false);
					setToast({ message: r.message, status: "Success", title: "Success" });
					navigate(`/audience-signal/view/${uniqueId}`);
				});
			})
			.catch((error) => {
				setLoading(false);
				if (isConcurrentChange(error)) {
					setToast(concurrentChangeToast(error));
					return;
				}
				const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
				const message = errors
					? errors.map((e) => e[1]).join("\n")
					: error.data?.message || error.message || "Failed to update signal audience";
				setToast({
					message: message,
					status: "Invalid",
					title: errors ? error.data.message : "Error",
				});
			});
	};

	// Saves through PUT /cohorts/<id> with activation_methods, the same path V2 uses, so GAM /
	// Xandr sync and DV360 slot assignment run exactly as they do for a custom audience.
	//
	// In edit mode this is the "Update & Push" path, so it must carry the FORM's values, not
	// just the stored ones. The payload is built from the stored cohort (to preserve fields
	// this page does not touch), then the edited name, description and threshold are written
	// over it. Without that override the slider value never reached the API and a success
	// toast still showed — the edit was silently discarded.
	const handleUpdateDestinations = async (selectedDestinations, networkMode, networkSubset, equativPrice) => {
		setDestinationsModalOpen(false);
		setLoading(true);
		setGamNetworkSelectionMode(networkMode);
		setGamNetworkSubset(networkSubset);
		try {
			const response = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
			const payload = {
				data: [
					{
						...response.data,
						activation_methods: selectedDestinations.map((d) => ({
							id: parseInt(d.id, 10),
							name: d.name,
							type: d.type,
						})),
						...(editMode && {
							name: audienceName,
							description: audienceDescription,
							signal_conditions: { affinity_score: selectedAffinity },
						}),
						gamNetworkSelectionMode: networkMode,
						gamNetworkSubset: networkMode === "subset" ? networkSubset : [],
						...(equativPrice != null && { equativPrice }),
						// The version this page loaded, not the re-read's (ASB-4801).
						...(loadedVersion != null && { version: loadedVersion }),
					},
				],
			};
			const updated = await (
				await updateCohort(payload, uniqueId, localStorage.getItem("activeSilo"))
			).json();
			if (updated?.dv360SlotError) {
				setToast({
					message: `${editMode ? "Audience and destinations" : "Destinations"} updated successfully, but ${updated.dv360SlotError}`,
					status: "Info",
					title: "DV360 slot not assigned",
				});
			} else {
				setToast({
					message: `${editMode ? "Audience and destinations" : "Destinations"} updated successfully${formatDv360SlotSuffix(updated?.dv360Slot)}`,
					status: "Success",
					title: "Success",
				});
			}
			// Re-fetch rather than trust selectedDestinations: it is sourced from the generic
			// /activationMethods catalog ({id, name, type, label}), so it never carries the
			// per-destination details object (e.g. Equativ's details.equativ.price, DV360's
			// slot). Setting activationMethods from it directly strips that detail for as long
			// as the page stays open, so the next time the Destinations modal opens it prefills
			// as if this were a fresh activation. In its own try/catch, after the success
			// toast above: the update already succeeded by this point, so a refetch failure
			// here must not surface as "Failed to update destinations" — worst case is a
			// stale local mirror until the next full page load.
			// Edit mode navigates away immediately, and the view page fetches on mount, so
			// refetching here would be thrown away -- and on a 5xx its retries would hold the
			// spinner for ~4s before a navigation that was always going to reload the data.
			// The version this save left, not the refetch's below (ASB-4801).
			if (updated?.version != null) setLoadedVersion(updated.version);
			if (editMode) {
				navigate(`/audience-signal/view/${uniqueId}`);
			} else {
				try {
					const refreshed = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
					setActivationMethods(refreshed.data.activation_methods ?? []);
				} catch (refreshError) {
					console.error("Failed to refresh activation methods after updating destinations:", refreshError);
					// Fall back to the modal's selection, preserving any details the generic
					// catalog stripped (Equativ price, DV360 slot). Without this, a refetch
					// failure leaves the pre-save list in place and the next save can undo
					// this one.
					setActivationMethods((prev) =>
						selectedDestinations.map((d) => {
							const details = prev.find((p) => p.id === d.id)?.details;
							// `prev` is the PRE-save list, so its price is the one this save
							// just replaced. Carrying it over would prefill the modal with a
							// price the server no longer holds -- the same stale-price reopen
							// this ticket exists to fix, reached by a different route.
							if (equativPrice != null && isEquativMethodId(d.id)) {
								return {
									...d,
									details: { ...details, equativ: { ...details?.equativ, price: equativPrice } },
								};
							}
							return details ? { ...d, details } : d;
						})
					);
				}
			}
		} catch (error) {
			if (isConcurrentChange(error)) {
				setToast(concurrentChangeToast(error));
				return;
			}
			setToast({
				message: error.data?.message || error.message || "Failed to update destinations",
				status: "Invalid",
				title: "Error",
			});
		} finally {
			setLoading(false);
		}
	};

	// Copy: the API validates exactly {base_cohort_id, affinity_score} on create, so a signal
	// audience round-trips. Useful for trying a second threshold against the same base audience.
	const handleCopy = async () => {
		if (!copyName.trim()) return;
		setActionLoading(true);
		try {
			const response = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
			const payload = {
				data: [
					{
						...response.data,
						cid: null,
						unique_id: null,
						name: copyName,
						auto_generated: false,
						tagId: parseInt(response.data.tagId),
						// A new audience starts its own versions (ASB-4801).
						version: undefined,
					},
				],
			};
			await createCohort(payload);
			setToast({ message: "Audience copied successfully", status: "Success", title: "Success" });
			setShowCopyModal(false);
			setCopyName("");
		} catch (error) {
			setToast({
				message: error.data?.message || error.message || "Failed to copy audience",
				status: "Invalid",
				title: "Error",
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleDelete = async () => {
		setActionLoading(true);
		try {
			await DeleteCohort([uniqueId]);
			setToast({ message: "Audience deleted successfully", status: "Success", title: "Success" });
			setShowDeleteModal(false);
			setTimeout(() => navigate("/audience-manager"), 1500);
		} catch (error) {
			setToast({
				message: error.data?.message || error.message || "Failed to delete audience",
				status: "Invalid",
				title: "Error",
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleArchiveToggle = async () => {
		setActionLoading(true);
		try {
			const response = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
			const payload = {
				data: [{
					...response.data,
					active: !isActive,
					// The version this page loaded, not the re-read's (ASB-4801).
					...(loadedVersion != null && { version: loadedVersion }),
				}],
			};
			const saved = await (await updateCohort(payload, uniqueId, localStorage.getItem("activeSilo"))).json();
			if (saved?.version != null) setLoadedVersion(saved.version);
			setIsActive(!isActive);
			setToast({
				message: `Audience ${isActive ? "archived" : "unarchived"} successfully`,
				status: "Success",
				title: "Success",
			});
			setShowArchiveModal(false);
		} catch (error) {
			if (isConcurrentChange(error)) {
				setShowArchiveModal(false);
				setToast(concurrentChangeToast(error));
				return;
			}
			setToast({
				message: error.data?.message || error.message || "Failed to update audience",
				status: "Invalid",
				title: "Error",
			});
		} finally {
			setActionLoading(false);
		}
	};

	// Same columns as the V2 export, with the signal rule in place of criteria.
	const handleExport = () => {
		const rows = [
			["Date Created", "Unique ID", "Audience Name", "Audience Type", "Base Audience", "Affinity", "Status"],
			[
				new Date().toISOString().split("T")[0],
				uniqueId,
				audienceName,
				"Signal",
				baseCohortName,
				`${selectedAffinity}+`,
				isActive ? "Active" : "Archived",
			],
		];
		const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], {
			type: "text/csv;charset=utf-8;",
		});
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", `cohort_${uniqueId}.csv`);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setToast({ message: "Export downloaded", status: "Success", title: "Success" });
	};

	const formatDate = (date) => {
		const formattedDate = Date.parse(String(date).replace("PM", "").replace("AM", ""));
		const format = new Date(formattedDate);
		return isNaN(format.getTime()) ? "Unknown" : format.toLocaleDateString();
	};

	const baseCohortName =
		cohort && cohort.signal_conditions
			? cohort.signal_conditions.base_cohort_name ?? `#${cohort.signal_conditions.base_cohort_id}`
			: "";

	// An unscored ArcTag is not a zero-reach audience. Say which one it is: a dash plus the
	// explanation, never a confident 0.
	const unavailable = sizing && sizing.status === "unavailable";
	const sized = sizing && sizing.status === "finished";

	const compact = (n) => Intl.NumberFormat("en-Us", { notation: "compact" }).format(n);

	if (!cohort) {
		return (
			<div className="wrapper">
				{toast && (
					<Toast
						status={toast.status}
						message={toast.message}
						title={toast.title}
						callback={() => toastClose()}
						autoHide={true}
					/>
				)}
			</div>
		);
	}

	return (
		<div className="wrapper">
			<form
				id="signal-edit"
				onSubmit={handleSubmit}
				onKeyDown={(e) => {
					e.key === "Enter" && e.target.tagName.toLowerCase() != "textarea"
						? e.preventDefault()
						: null;
				}}
			>
				<AudienceHeader
					name={audienceName}
					onNameChange={editMode ? setAudienceName : undefined}
					description={audienceDescription}
					onDescriptionChange={editMode ? setAudienceDescription : undefined}
					id={cohort.unique_id}
					destinationsCount={activationMethods.length}
					onConfigureDestinations={canManageDestinations ? () => setDestinationsModalOpen(true) : undefined}
					menu={
						editMode
							? undefined
							: [
									// Copy is deliberately NOT offered to read-only users. V2 currently does
									// offer it to them; that is ASB-4023 and is not carried over here.
									...(!readOnly
										? [
												{
													label: "Copy",
													onClick: () => {
														setCopyName(`${audienceName} (Copy)`);
														setShowCopyModal(true);
													},
												},
										  ]
										: []),
									...(canManageLifecycle
										? [{ label: "Delete", onClick: () => setShowDeleteModal(true) }]
										: []),
									{ label: "Export", onClick: handleExport },
									...(canManageLifecycle
										? [
												{
													label: isActive ? "Archive" : "Unarchive",
													onClick: () => setShowArchiveModal(true),
												},
										  ]
										: []),
							  ]
					}
					actions={
						editMode ? (
							<>
								<Button
									text="Cancel"
									type="button"
									style="light"
									onClick={() => navigate("/audience-manager")}
									disabled={false}
									loaded={false}
								/>
								<Button
									text={canManageDestinations ? "Update Only" : "Update"}
									type="submit"
									style={canManageDestinations ? "light" : "dark"}
									disabled={loading}
									loaded={loading}
								/>
								{canManageDestinations && (
									<Button
										text="Update & Push"
										type="button"
										style="dark"
										disabled={loading}
										onClick={() => setDestinationsModalOpen(true)}
										loaded={false}
									/>
								)}
							</>
						) : (
							<>
								{!readOnly && (
									<Button
										text="Edit"
										type="button"
										style="light"
										onClick={() => navigate(`/audience-signal/edit/${uniqueId}`)}
										disabled={false}
										loaded={false}
									/>
								)}
								<Button
									text="Continue to Manager"
									type="button"
									style="dark"
									onClick={() => navigate("/audience-manager")}
									disabled={false}
									loaded={false}
								/>
							</>
						)
					}
				/>
				<MetricsBar
					metrics={[
						{ label: "Unique Users", value: sized ? compact(sizing.uniqueUsers) : null },
						{ label: "Page Views", value: sized ? compact(sizing.pageViews) : null },
					]}
					loading={sizingLoading}
					qualifier="Estimated"
					qualifierTip="30-day estimate for the selected affinity, from the last affinity scoring run"
					actions={
						!unavailable && sizing && sizing.calculatedAt ? (
							<span className="p4">Affinity scores updated on {formatDate(sizing.calculatedAt)}</span>
						) : null
					}
					emptyMessage={
						unavailable
							? "Affinity scores have not been calculated for this ArcTag yet, so reach cannot be estimated. Please reach out to your ArcSpan representative."
							: null
					}
				/>
				<div className="container">
					<div className="container--inner signal-cards">
						<BuilderCard
							title={editMode ? "Adjust Affinity" : "Affinity"}
							info={AFFINITY_INFO}
						>
							{editMode ? (
								<Slider
									name="affinity-score"
									value={selectedAffinity}
									marks={affinitySliderValues}
									formatLabel={(v) => `${v}+`}
									onChange={(v) => setSelectedAffinity(v)}
								/>
							) : (
								<p className="p3 signal-card-value">{selectedAffinity}+</p>
							)}
						</BuilderCard>
						<BuilderCard title="Base Audience">
							<p className="p3 signal-card-value">{baseCohortName}</p>
						</BuilderCard>
					</div>
				</div>
			</form>
			<DestinationsModal
				isOpen={destinationsModalOpen}
				onClose={() => setDestinationsModalOpen(false)}
				onConfirm={handleUpdateDestinations}
				loading={loading}
				saveButtonText="Update Destinations"
				initialSelected={activationMethods}
				gamNetworkCodes={gamNetworkCodes}
				initialGamNetworkMode={gamNetworkSelectionMode}
				initialGamNetworkSubset={gamNetworkSubset}
				allowEquativPricePrompt={true}
			/>

			{showCopyModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>Copy Audience</h2>
						<p>Enter a name for the new audience:</p>
						<BasicInput
							name="copyName"
							customClasses="copy-name-input"
							required={true}
							disabled={false}
							placeholder="New audience name"
							value={copyName}
							onInput={(e) => setCopyName(e.target.value)}
						/>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => [setShowCopyModal(false), setCopyName("")]}
								disabled={actionLoading}
								loaded={false}
							/>
							<Button
								text="Copy"
								type="button"
								style="dark"
								onClick={handleCopy}
								disabled={!copyName.trim() || actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}

			{showDeleteModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>Delete Audience</h2>
						<p>Are you sure you want to permanently delete this audience?</p>
						<p className="audience-name-display">{audienceName}</p>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => setShowDeleteModal(false)}
								disabled={actionLoading}
								loaded={false}
							/>
							<Button
								text="Delete"
								type="button"
								style="dark"
								onClick={handleDelete}
								disabled={actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}

			{showArchiveModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>{isActive ? "Archive" : "Unarchive"} Audience</h2>
						<p>Are you sure you want to {isActive ? "archive" : "unarchive"} this audience?</p>
						<p className="audience-name-display">{audienceName}</p>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => setShowArchiveModal(false)}
								disabled={actionLoading}
								loaded={false}
							/>
							<Button
								text={isActive ? "Archive" : "Unarchive"}
								type="button"
								style="dark"
								onClick={handleArchiveToggle}
								disabled={actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}

			{toast && (
				<Toast
					// A new toast is a new instance: an earlier toast's auto-hide timer must not
					// close a refusal that is meant to stay (ASB-4801).
					key={`${toast.title}|${toast.message}`}
					status={toast.status}
					message={toast.message}
					title={toast.title}
					callback={() => toastClose()}
					autoHide={toast.autoHide ?? true}
					action={toast.action}
				/>
			)}
		</div>
	);
}

function SignalEdit({}) {
	return <Signal editMode={true} />;
}

function SignalView({}) {
	return <Signal editMode={false} />;
}

Signal.propTypes = {
	editMode: PropTypes.bool.isRequired,
};

export { SignalEdit, SignalView };
